/**
 * JS Style guide.
 * 
 * Stores lots of variables that relate to the site's style guide.
 */

/** Design system ------------------------------------------- */

/**
 * GENERAL TODOS
 * 
 * - Make everything an array of objects.
 *   We could then use getter functions to return things by key or turn them into options.
 * 
 * - Think through whitelisting, blacklisting, and re-mapping labels, etc. for these.
 * 
 * - Think through unified way to send dynamic responsive styles to components
 * 
 * - Consider HOC to emulate VueJS' nice syntax for conditional rendering
 */

/**
 * The atomic spacing.
 */
export const atomicSpacing = 8

/**
 * Responsive Breakpoints.
 * Assumes mobile-first approach with each being inclusive min-width.
 */
export const breakpoints = {
    default: {
        label     : 'Mobile (Default)',
        threshold : 0
    },
    portraitTablet: {
        label     : 'Tablet (Portrait)',
        threshold : 768
    },
    landscapeTablet: {
        label     : 'Tablet (Landscape)',
        threshold : 1024
    },
    laptop: {
        label     : 'Laptop',
        threshold : 1280,
    },
    desktop: {
        label     : 'Desktop',
        threshold : 1440
    },
    lg: {
        label     : 'Large',
        threshold : 1920
    },
    xl: {
        label     : 'Extra Large',
        threshold : 2560
    },
    longBoi: {
        label     : 'Long Boi',
        threshold : 3440
    }
}

/**
 * The default breakpoint.
 */
export const defaultBreakpoint = 'default'

/**
 * The breakpoint best associated with the editor experience.
 */
export const editorExperienceBreakpoint = 'laptop'

/**
 * Color options.
 * 
 * All brand colors in the theme.
 * 
 * TODO: Make this an array of objects.
 * VISION: Make these accept colorBox params.
 */
export const colors = {

    // Expression

    // Roman Purple

    'expressionRomanPurplePlus7' : { hex: '#985ba6', label: 'Roman Purple +7' },
    'expressionRomanPurplePlus6' : { hex: '#8f4e9d', label: 'Roman Purple +6' },
    'expressionRomanPurplePlus5' : { hex: '#864195', label: 'Roman Purple +5' },
    'expressionRomanPurplePlus4' : { hex: '#7d368c', label: 'Roman Purple +4' },
    'expressionRomanPurplePlus3' : { hex: '#742b84', label: 'Roman Purple +3' },
    'expressionRomanPurplePlus2' : { hex: '#641c74', label: 'Roman Purple +2' },
    'expressionRomanPurplePlus1' : { hex: '#550f64', label: 'Roman Purple +1' },
    'expressionRomanPurple0'     : { hex: '#460554', label: 'Roman Purple 0 (Base)' },
    'expressionRomanPurpleMinus1': { hex: '#3b0347', label: 'Roman Purple -1' },
    'expressionRomanPurpleMinus2': { hex: '#30013b', label: 'Roman Purple -2' },
    'expressionRomanPurpleMinus3': { hex: '#25002e', label: 'Roman Purple -2' },

    // Blue

    'expressionBluePlus7' : { hex : '#b8dbff', label: 'Blue +7' },
    'expressionBluePlus6' : { hex : '#9ec9f5', label: 'Blue +6' },
    'expressionBluePlus5' : { hex : '#86b8eb', label: 'Blue +5' },
    'expressionBluePlus4' : { hex : '#6fa8e1', label: 'Blue +4' },
    'expressionBluePlus3' : { hex : '#5998d7', label: 'Blue +3' },
    'expressionBluePlus2' : { hex : '#407fbf', label: 'Blue +2' },
    'expressionBluePlus1' : { hex : '#2a68a6', label: 'Blue +1' },
    'expressionBlue0'     : { hex : '#16528e', label: 'Blue 0 (Base)' },
    'expressionBlueMinus1': { hex : '#1d4267', label: 'Blue -1' },
    'expressionBlueMinus2': { hex : '#162b40', label: 'Blue -2' },
    'expressionBlueMinus3': { hex : '#050f19', label: 'Blue -3' },

    // Touch of Dawn

    'expressionTouchOfDawnPlus6' : { hex: '#fafcff', label: 'Touch of Dawn +6' },
    'expressionTouchOfDawnPlus5' : { hex: '#f5f9ff', label: 'Touch of Dawn +5' },
    'expressionTouchOfDawnPlus4' : { hex: '#f1f6fe', label: 'Touch of Dawn +4' },
    'expressionTouchOfDawnPlus3' : { hex: '#ecf3fe', label: 'Touch of Dawn +3' },
    'expressionTouchOfDawnPlus2' : { hex: '#e7f0fd', label: 'Touch of Dawn +2' },
    'expressionTouchOfDawnPlus1' : { hex: '#deeafd', label: 'Touch of Dawn +1' },
    'expressionTouchOfDawn0'     : { hex: '#d4e4fc', label: 'Touch of Dawn 0 (Base)' },
    'expressionTouchOfDawnMinus1': { hex: '#c9defd', label: 'Touch of Dawn -1' },
    'expressionTouchOfDawnMinus2': { hex: '#bed8fe', label: 'Touch of Dawn -2' },
    'expressionTouchOfDawnMinus3': { hex: '#b3d1fe', label: 'Touch of Dawn -3' },
    'expressionTouchOfDawnMinus4': { hex: '#a8cbff', label: 'Touch of Dawn -4' },

    // Neutral

    // Light

    'neutralLightWhite' : { hex: '#ffffff', label: 'White' },
    'neutralLightPlus2' : { hex: '#f6f6fb', label: 'Neutral Light +2' },
    'neutralLightPlus1' : { hex: '#f0f0f6', label: 'Neutral Light +1' },
    'neutralLight0'     : { hex: '#e9e9f2', label: 'Neutral Light 0 (Base)' },
    'neutralLightMinus1': { hex: '#dadae9', label: 'Neutral Light -1' },
    'neutralLightMinus2': { hex: '#d3d3e4', label: 'Neutral Light -2' },
    'neutralLightMinus3': { hex: '#c5c5db', label: 'Neutral Light -3' },

    'neutralDarkPlus7': { hex: '#6d6d85', label: 'Neutral Dark +7' },
    'neutralDarkPlus6': { hex: '#606179', label: 'Neutral Dark +6' },
    'neutralDarkPlus5': { hex: '#53566e', label: 'Neutral Dark +5' },
    'neutralDarkPlus4': { hex: '#484c63', label: 'Neutral Dark +4' },
    'neutralDarkPlus3': { hex: '#3d4358', label: 'Neutral Dark +3' },
    'neutralDarkPlus2': { hex: '#292f40', label: 'Neutral Dark +2' },
    'neutralDarkPlus1': { hex: '#181d29', label: 'Neutral Dark +1' },
    'neutralDark0'    : { hex: '#090c11', label: 'Neutral Dark 0 (Base)' },
    'neutralDarkBlack': { hex: '#000000', label: 'Black' },

    // Semantic

    // Success

    'semanticSuccessPlus6' : { hex: '#eaf7e1', label: 'Semantic Success +6' },
    'semanticSuccessPlus5' : { hex: '#c8e8b3', label: 'Semantic Success +5' },
    'semanticSuccessPlus4' : { hex: '#a9da88', label: 'Semantic Success +4' },
    'semanticSuccessPlus3' : { hex: '#8ccb62', label: 'Semantic Success +3' },
    'semanticSuccessPlus2' : { hex: '#71bc40', label: 'Semantic Success +2' },
    'semanticSuccessPlus1' : { hex: '#519f1e', label: 'Semantic Success +1' },
    'semanticSuccess0'     : { hex: '#358103', label: 'Semantic Success 0 (Base)' },
    'semanticSuccessMinus1': { hex: '#356714', label: 'Semantic Success -1' },
    'semanticSuccessMinus2': { hex: '#2f4c1c', label: 'Semantic Success -2' },
    'semanticSuccessMinus3': { hex: '#24321a', label: 'Semantic Success -3' },
    'semanticSuccessMinus4': { hex: '#131710', label: 'Semantic Success -4' },

    // Error

    'semanticErrorPlus6' : { hex: '#f7e4df', label: 'Semantic Error +6' },
    'semanticErrorPlus5' : { hex: '#f6cbbe', label: 'Semantic Error +5' },
    'semanticErrorPlus4' : { hex: '#f5b29d', label: 'Semantic Error +4' },
    'semanticErrorPlus3' : { hex: '#f4997c', label: 'Semantic Error +3' },
    'semanticErrorPlus2' : { hex: '#f2805c', label: 'Semantic Error +2' },
    'semanticErrorPlus1' : { hex: '#e3592d', label: 'Semantic Error +1' },
    'semanticError0'     : { hex: '#d43604', label: 'Semantic Error 0 (Base)' },
    'semanticErrorMinus1': { hex: '#a52c06', label: 'Semantic Error -1' },
    'semanticErrorMinus2': { hex: '#752106', label: 'Semantic Error -2' },
    'semanticErrorMinus3': { hex: '#461405', label: 'Semantic Error -3' },
    'semanticErrorMinus4': { hex: '#170702', label: 'Semantic Error -4' },

    // Utility

    'transparent'     : { hex: '#00000000', label: 'Transparent' },
    'contextSurface'  : { hex: '#f6f6fb00', label: 'Contextual Surface' },
    'contextPrimary'  : { hex: '#46055400', label: 'Contextual Primary' },
    'contextSecondary': { hex: '#16528e00', label: 'Contextual Secondary' },
    'contextTertiary' : { hex: '#d4e4fc00', label: 'Contextual Tertiary' }
}

/** Shared property options --------------------------------- */

/**
 * The null option.
 * 
 * Normalized property for null/default options.
 */
export const nullOption = { value: '', label: '— Select —' }

/**
 * Side options.
 * 
 * Represents possible values for any component that may have a box-model-based property that applies to a side.
 */
export const sides = [
    { value: 'top',    cssValue: 'top',    label: 'Top' },
    { value: 'right',  cssValue: 'right',  label: 'Right' },
    { value: 'bottom', cssValue: 'bottom', label: 'Bottom' },
    { value: 'left',   cssValue: 'left',   label: 'Left' },
]

/**
 * Horizontal alignment options.
 * 
 * Represents possible values for any component that may have a horizontal alignment property.
 */
export const horizontalAlignments = [
	{ value: 'stretch', cssValue: 'stretch',       label: 'Stretch' },
    { value: 'left',    cssValue: 'start',         label: 'Left' },
    { value: 'center',  cssValue: 'center',        label: 'Center' },
    { value: 'right',   cssValue: 'end',           label: 'Right' },
    { value: 'between', cssValue: 'space-between', label: 'Space Between' },
    { value: 'around',  cssValue: 'space-around',  label: 'Space Around' }
]

/**
 * Vertical alignment options.
 * 
 * Represents possible values for any component that may have a vertical alignment property.
 */
export const verticalAlignments = [
    { value: 'stretch', cssValue: 'stretch',       label: 'Stretch' },
    { value: 'top',     cssValue: 'start',         label: 'Top' },
    { value: 'center',  cssValue: 'center',        label: 'Center' },
    { value: 'bottom',  cssValue: 'end',           label: 'Bottom' },
    { value: 'between', cssValue: 'space-between', label: 'Space Between' },
    { value: 'around',  cssValue: 'space-around',  label: 'Space Around' }
]

/**
 * Matrix alignment options.
 * 
 * Represents possible values for any component that may have a matrix alignment property.
 */
export const matrixAlignments = [
    { value: 'topLeft',      cssValue: 'top left',      label: 'Top Left' },
    { value: 'topCenter',    cssValue: 'top center',    label: 'Top Center' },
    { value: 'topRight',     cssValue: 'top right',     label: 'Top Right' },
    { value: 'centerLeft',   cssValue: 'center left',   label: 'Center Left' },
    { value: 'center',       cssValue: 'center',        label: 'Center' },
    { value: 'centerRight',  cssValue: 'center right',  label: 'Center Right' },
    { value: 'bottomLeft',   cssValue: 'bottom left',   label: 'Bottom Left' },
    { value: 'bottomCenter', cssValue: 'bottom center', label: 'Bottom Center' },
    { value: 'bottomRight',  cssValue: 'bottom right',  label: 'Bottom Right' },
]

/**
 * Fit options.
 * 
 * Represents possible values for any component that may have an object fit property.
 */
export const fits = [
    { value: 'contain',   cssValue: 'contain',    label: 'Contain' },
    { value: 'cover',     cssValue: 'cover',      label: 'Cover' },
    { value: 'fill',      cssValue: 'fill',       label: 'Fill' },
    { value: 'none',      cssValue: 'none',       label: 'None' },
    { value: 'scaleDown', cssValue: 'scale-down', label: 'Scale Down' }
]

/**
 * Named spacing options.
 * 
 * Represents all named spacings in the theme.
 */
export const spacings = [
    { value: 'none', label: 'None' },
    { value: 'atomic', label: 'Atomic' },
    { value: 'molecule', label: 'Molecule' },
    { value: 'module', label: 'Module' },
    { value: 'block', label: 'Block' },
    { value: 'section', label: 'Section' }
]

/**
 * Named color context options.
 * 
 * Represents all named color contexts in the theme.
 */
export const colorContexts = [
    { value: 'neutralLightWhite',          label: 'White',                  color: 'neutralLightWhite' },
    { value: 'neutralLightPlus2',          label: 'Neutral Light +2',       color: 'neutralLightPlus2' },
	{ value: 'expressionTouchOfDawn0',     label: 'Touch of Dawn 0 (Base)', color: 'expressionTouchOfDawn0' },
	{ value: 'expressionTouchOfDawnPlus5', label: 'Touch of Dawn +5',       color: 'expressionTouchOfDawnPlus5' },
    { value: 'expressionRomanPurple0',     label: 'Roman Purple 0 (Base)',  color: 'expressionRomanPurple0' }
]

/**
 * Named radii options.
 * 
 * Represents all named radii in the theme.
 */
export const radii = [
    { value: 'none', label: 'None' },
    { value: 'sm', label: 'Small' },
    { value: 'md', label: 'Medium' },
    { value: 'lg', label: 'Large' },
]

/** Getters ------------------------------------------------- */

/**
 * Generalized getter function for shared properties.
 */
export function getSharedOptions( options, whitelist = [], includeNull = true, includeCustom = false ) {

    if ( !! whitelist.length  ) {
        options = options.filter( ( option ) => whitelist.indexOf( option.value ) !== -1 )
    }

    if ( includeNull ) {
        options = [ nullOption, ...options ]
    }

    if ( includeCustom ) {
        options = [ ...options, customOption ]
    }

    return options
}

/**
 * Generalized getter function for singular shared properties.
 */
export function getSharedOption( options, value ) {
    return options.find( ( option ) => option.value === value )
}

/**
 * Gets horizontal alignments.
 * 
 * @see getSharedOptions()
 */
export function getSides( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( sides, whitelist, includeNull, includeCustom );
}

/**
 * Gets horizontal alignments.
 * 
 * @see getSharedOptions()
 * @see getSharedOption()
 */
export function getHorizontalAlignments( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( horizontalAlignments, whitelist, includeNull, includeCustom );
}
export function getHorizontalAlignment( value ) {
    return getSharedOption( horizontalAlignments, value )
}

/**
 * Gets vertical alignments.
 * 
 * @see getSharedOptions()
 * @see getSharedOption()
 */
export function getVerticalAlignments( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( verticalAlignments, whitelist, includeNull, includeCustom );
}
export function getVerticalAlignment( value ) {
    return getSharedOption( verticalAlignments, value )
}

/**
 * Gets matrix alignments.
 * 
 * @see getSharedOptions()
 */
export function getMatrixAlignments( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( matrixAlignments, whitelist, includeNull, includeCustom );
}

/**
 * Gets fits.
 * 
 * @see getSharedOptions()
 */
export function getFits( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( fits, whitelist, includeNull, includeCustom );
}

/**
 * Gets named spacings.
 * 
 * @see getSharedOptions()
 */
export function getSpacings( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( spacings, whitelist, includeNull, includeCustom );
}

/**
 * Gets color contexts.
 * 
 * @see getSharedOptions()
 */
export function getColorContexts( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( colorContexts, whitelist, includeNull, includeCustom )
}

/**
 * Gets radii.
 * 
 * @see getSharedOptions()
 */
export function getRadii( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( radii, whitelist, includeNull, includeCustom )
}
 